import "./SealConfirmationPage.scss";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Paths } from "../../../paths";
import { AppSeo } from "../../../components/app-seo/AppSeo";
import { REGISTRATION_FEE_DOLLARS } from "./constants/sealRegistrationConstants";
import { SealRegistrationStepIndicator } from "./SealRegistrationStepIndicator";
import {
  fetchRegistrationBySessionId,
  SealRegistrationRecord,
} from "../../../api/seal-registration-api";

// Helper to format role keys into labels
const ROLE_LABELS: Record<string, string> = {
  vineyardOwner: "Vineyard owner/operator",
  producer: "Wine producer",
  brandOwner: "Wine brand owner",
  importer: "Importer",
  retailer: "Retailer",
};

const USE_LABELS: Record<string, string> = {
  packaging: "Packaging",
  website: "Website/e-commerce",
  marketing: "Marketing materials",
  shelfTalkers: "Shelf talkers/displays",
  wineLists: "Wine lists",
  signage: "Signage",
  otherPromo: "Other promotional",
};

const formatRoles = (rolesString: string): string =>
  rolesString
    .split(",")
    .map((r) => ROLE_LABELS[r.trim()] || r.trim())
    .join(", ");

const formatUses = (usesString: string): string =>
  usesString
    .split(",")
    .map((u) => USE_LABELS[u.trim()] || u.trim())
    .join(", ");

const getCategory = (rolesString: string): string => {
  const roles = rolesString.split(",").map((r) => r.trim());
  if (roles.includes("brandOwner") || roles.includes("producer")) return "Wine brand";
  if (roles.includes("vineyardOwner")) return "Vineyard business";
  if (roles.includes("importer")) return "Importing business";
  if (roles.includes("retailer")) return "Retail business";
  return "Organization";
};

const parseVineyardUrls = (urlsJson: string): string[] => {
  try {
    return JSON.parse(urlsJson);
  } catch {
    return urlsJson.split(",").filter((u) => u.trim() !== "");
  }
};

export const SealConfirmationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");

  const [registration, setRegistration] = useState<SealRegistrationRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showVineyards, setShowVineyards] = useState(false);
  const [showWineNotes, setShowWineNotes] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      setError(true);
      return;
    }

    fetchRegistrationBySessionId(sessionId)
      .then((data) => {
        setRegistration(data);
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [sessionId]);

  if (loading) {
    return (
      <div className="about-layout">
        <div className="seal-registration-content">
          <SealRegistrationStepIndicator currentStep={3} />
          <div className="seal-confirmation-no-data">
            <p className="form-fields-sub-text" style={{ fontSize: "17px" }}>
              Loading your registration confirmation...
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !registration) {
    return (
      <div className="about-layout">
        <div className="seal-registration-content">
          <SealRegistrationStepIndicator currentStep={3} />
          <div className="seal-confirmation-no-data">
            <p className="form-fields-sub-text" style={{ fontSize: "17px" }}>
              We could not find the registration details for this confirmation.
            </p>
            <button
              className="button"
              style={{ height: "52px", padding: "0 28px" }}
              onClick={() => navigate(Paths.sealRegister)}
            >
              Return to OVR Seal registration
            </button>
          </div>
        </div>
      </div>
    );
  }

  const category = getCategory(registration.roles);
  const isTradeCategory = category === "Importing business" || category === "Retail business";
  const isWineBrandCategory = category === "Wine brand" || category === "Vineyard business";
  const packagingRequested = registration.intendedUses.includes("packaging");
  const vineyardUrls = parseVineyardUrls(registration.vineyardUrls);
  const hasWineNotes = registration.wineNotes && registration.wineNotes.trim() !== "";
  const donationDollars = (registration.donationAmountCents / 100).toFixed(2);
  const totalDollars = (registration.totalAmountCents / 100).toFixed(2);
  const receiptDate = new Date(registration.createdDate).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <>
      <AppSeo
        title="OVR Seal Registration Confirmation"
        description="Your OVR Seal registration is confirmed. Review your registration details and next steps."
        robots="noindex, nofollow"
      />
      <div className="about-layout">
        <div className="seal-registration-content">
          <SealRegistrationStepIndicator currentStep={3} />

          <div className="seal-confirmation-hero">
            <div className="seal-confirmation-check">&#10003;</div>
            <h1 className="title-large" style={{ textAlign: "center" }}>
              Registration received
            </h1>
            <p
              className="description-text"
              style={{ textAlign: "center", marginBottom: "16px" }}
            >
              Thank you for registering {registration.registeredName} for the OVR Seal
              program. We have received your registration and payment.
            </p>
            <div className="seal-status-badge">
              Status: Awaiting OVR review
            </div>
            <p className="description-text" style={{ textAlign: "center" }}>
              Review and vineyard verification may take 1–3 weeks.
            </p>
            <p className="seal-confirmation-warning">
              {registration.registeredName} is not yet authorized to use the OVR Seal.
              Please wait until OVR confirms the registration and provides the
              official seal artwork and Usage Guidelines.
            </p>
          </div>

          {/* Scope notice */}
          <div className="seal-confirmation-scope">
            <p className="description-text">
              This registration applies only to {registration.registeredName}. It does
              not authorize use by another wine brand or organization.
            </p>
            {isWineBrandCategory && (
              <p className="description-text">
                Other separately marketed wine brands require separate
                registrations, even when they share the same parent company,
                producer, owner, or management.
              </p>
            )}
            {isTradeCategory && (
              <p className="description-text" style={{ margin: 0 }}>
                This registration does not register the wine brands carried by{" "}
                {registration.registeredName} and does not authorize those brands or
                their producers to use the seal on packaging.
              </p>
            )}
          </div>

          {/* What happens next */}
          <div className="seal-form-section seal-form-section--bordered">
            <h2 className="sub-headline">What happens next?</h2>
            <ol className="seal-next-steps-list">
              <li>
                OVR will review the submitted registration and vineyard
                information.
              </li>
              <li>
                The review and vineyard verification process may take 1–3 weeks.
              </li>
              <li>
                If additional information is needed, OVR will contact{" "}
                {registration.fullName} at {registration.email}.
              </li>
              <li>
                Once the registration is confirmed, OVR will provide the
                official seal artwork and current Usage Guidelines appropriate to
                the registered participant's role.
              </li>
              <li>
                {registration.registeredName} may begin using the seal only after
                receiving confirmation from OVR.
              </li>
            </ol>
            <p className="form-fields-sub-text" style={{ marginTop: "16px" }}>
              Questions about your registration? Contact{" "}
              <a
                href="mailto:oldvineregistry@gmail.com"
                style={{ color: "#215d9c", textDecoration: "underline" }}
              >
                oldvineregistry@gmail.com
              </a>
              .
            </p>
          </div>

          {/* Registration details */}
          <div className="seal-form-section seal-form-section--bordered seal-confirmation-print-area">
            <h2 className="sub-headline">Registration details</h2>
            <div className="seal-summary-card" style={{ marginBottom: "0" }}>
              <SummaryRow label="Name registered" value={registration.registeredName} />
              <SummaryRow label="Registration category" value={category} />
              <SummaryRow label="Legal organization" value={registration.legalOrganizationName} />
              <SummaryRow label="Applicant" value={registration.fullName} />
              <SummaryRow label="Applicant email" value={registration.email} />
              <SummaryRow
                label="Selected role(s)"
                value={formatRoles(registration.roles)}
              />
              <SummaryRow
                label="Intended seal use"
                value={formatUses(registration.intendedUses)}
              />
              <SummaryRow
                label="Vineyard records submitted"
                value={String(vineyardUrls.length)}
              />
              <SummaryRow label="Submission date" value={receiptDate} />
              <SummaryRow
                label="Registration reference"
                value={`OVR-SEAL-${String(registration.id).padStart(5, "0")}`}
              />
              <SummaryRow
                label="Registration status"
                value="Awaiting OVR review"
                valueStyle={{ color: "#8a6d3b", fontWeight: 700 }}
              />
              <SummaryRow
                label="Estimated review time"
                value="1–3 weeks"
              />

              {registration.conferenceInterest && (
                <div className="seal-confirmation-note-info">
                  Requested contact about Old Vine Conference membership
                </div>
              )}

              {packagingRequested && (
                <div className="seal-confirmation-note-neutral">
                  Packaging use requested — awaiting OVR review
                </div>
              )}

              {/* Vineyard URLs expandable */}
              <div className="seal-summary-card-vineyards">
                <span
                  className="form-fields-sub-text form-fields-sub-text-link"
                  onClick={() => setShowVineyards(!showVineyards)}
                >
                  {showVineyards ? "Hide vineyard URLs" : "Show vineyard URLs"}
                </span>
                {showVineyards && (
                  <ul>
                    {vineyardUrls.map((url, i) => (
                      <li key={i}>{url}</li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Wine notes expandable */}
              {hasWineNotes && (
                <div>
                  <span
                    className="form-fields-sub-text form-fields-sub-text-link"
                    onClick={() => setShowWineNotes(!showWineNotes)}
                  >
                    {showWineNotes
                      ? "Hide wine/vintage notes"
                      : "Show wine/vintage notes"}
                  </span>
                  {showWineNotes && (
                    <p
                      className="form-fields-sub-text"
                      style={{
                        whiteSpace: "pre-wrap",
                        margin: "10px 0 0",
                      }}
                    >
                      {registration.wineNotes}
                    </p>
                  )}
                  <p
                    className="form-fields-sub-text"
                    style={{ fontSize: "13px", marginTop: "6px" }}
                  >
                    Individual wines and vintages are not separately registered
                    or approved.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Communication preferences */}
          <div className="seal-form-section seal-form-section--bordered">
            <h2 className="sub-headline">Communication preferences</h2>
            <div className="seal-confirmation-comms-card">
              <div className="seal-confirmation-comms-text">
                Program administration: OVR may send necessary registration,
                review, license, guideline, and compliance communications to{" "}
                {registration.email}.
              </div>
              <div className="seal-confirmation-comms-status">
                Optional OVR communications:{" "}
                {registration.communicationsOptional ? "Subscribed" : "Not subscribed"}
              </div>
            </div>
          </div>

          {/* Payment confirmation */}
          <div className="seal-form-section seal-form-section--bordered seal-confirmation-print-area">
            <h2 className="sub-headline">Payment confirmation</h2>
            <div className="seal-confirmation-payment-card">
              <div className="seal-confirmation-payment-row">
                <span>Registration reference</span>
                <span style={{ fontWeight: 700 }}>{`OVR-SEAL-${String(registration.id).padStart(5, "0")}`}</span>
              </div>
              <div className="seal-confirmation-payment-row">
                <span>Registered name</span>
                <span>{registration.registeredName}</span>
              </div>
              <div className="seal-confirmation-payment-row">
                <span>OVR Seal registration fee</span>
                <span>US ${REGISTRATION_FEE_DOLLARS.toFixed(2)}</span>
              </div>
              <div className="seal-confirmation-payment-row">
                <span>Optional additional donation</span>
                <span>US ${donationDollars}</span>
              </div>
              <div className="seal-confirmation-payment-total">
                <span>Total paid</span>
                <span>US ${totalDollars}</span>
              </div>
              <div className="seal-confirmation-payment-row" style={{ paddingTop: "4px" }}>
                <span>Payment date</span>
                <span>{receiptDate}</span>
              </div>
              {registration.cardLastFour && (
                <div className="seal-confirmation-payment-row">
                  <span>Payment method</span>
                  <span>Card ending in {registration.cardLastFour}</span>
                </div>
              )}
            </div>
            <p
              className="form-fields-sub-text"
              style={{ fontSize: "13px", marginTop: "10px" }}
            >
              This payment submitted the registration for review. It does not
              constitute approval or authorization to use the OVR Seal.
            </p>
            <span
              className="form-fields-sub-text form-fields-sub-text-link"
              onClick={() => {
                const printAreas = document.querySelectorAll('.seal-confirmation-print-area');
                const wrapper = document.createElement('div');
                wrapper.className = 'about-layout';
                const inner = document.createElement('div');
                inner.className = 'seal-registration-content';
                wrapper.appendChild(inner);
                printAreas.forEach(area => {
                  const clone = area.cloneNode(true) as HTMLElement;
                  clone.querySelectorAll('.seal-no-print').forEach(el => el.remove());
                  inner.appendChild(clone);
                });
                const bodyChildren = Array.from(document.body.children) as HTMLElement[];
                bodyChildren.forEach(child => child.style.display = 'none');
                document.body.appendChild(wrapper);
                window.print();
                document.body.removeChild(wrapper);
                bodyChildren.forEach(child => child.style.display = '');
              }}
              style={{ display: "inline-block", marginTop: "14px" }}
            >
              Print or save confirmation
            </span>
          </div>

          {/* Return and links */}
          <button
            className="button seal-submit-button"
            style={{ marginTop: "28px" }}
            onClick={() => navigate("/")}
          >
            Return to the Old Vine Registry
          </button>
          <div className="seal-confirmation-links">
            <a
              href={Paths.advancedSearch}
              onClick={(e) => {
                e.preventDefault();
                navigate(`${Paths.advancedSearch}?search=false`);
              }}
              className="description-text-link"
            >
              Search registered vineyards
            </a>
            <a
              href={Paths.sealGuidelines}
              onClick={(e) => {
                e.preventDefault();
                navigate(Paths.sealGuidelines);
              }}
              className="description-text-link"
            >
              Read the OVR Seal Usage Guidelines
            </a>
            <a
              href={Paths.sealFaq}
              onClick={(e) => {
                e.preventDefault();
                navigate(Paths.sealFaq);
              }}
              className="description-text-link"
            >
              View OVR Seal FAQs
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

const SummaryRow: React.FC<{
  label: string;
  value: string;
  valueStyle?: React.CSSProperties;
}> = ({ label, value, valueStyle }) => (
  <div className="seal-summary-row">
    <span className="seal-summary-row-label">{label}</span>
    <span className="seal-summary-row-value" style={valueStyle}>
      {value}
    </span>
  </div>
);
